import { useCallback, useEffect, useState } from 'react'
import type { ProjectBoardDocument, ProjectTaskDocument } from '@/types'
import { toUserFacingError } from '@/lib/errors'
import { projectBoardsService } from '../services/projectBoards.service'

type LoadedBoard = ProjectBoardDocument & { id: string }
type LoadedTask = ProjectTaskDocument & { id: string }

export function useProjectBoard(boardId: string | null) {
  const [board, setBoard] = useState<LoadedBoard | null>(null)
  const [tasks, setTasks] = useState<LoadedTask[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const reload = useCallback(async () => {
    if (!boardId) {
      setBoard(null)
      setTasks([])
      return
    }
    setLoading(true)
    try {
      const [b, t] = await Promise.all([
        projectBoardsService.getBoard(boardId),
        projectBoardsService.listTasks(boardId),
      ])
      setBoard(b ? { ...b, id: boardId } : null)
      setTasks(t)
      setError(null)
    } catch (e) {
      setError(toUserFacingError(
        e,
        'We could not load this project board. Please try again.',
        'project-board/load',
      ))
    } finally {
      setLoading(false)
    }
  }, [boardId])

  useEffect(() => {
    void reload()
  }, [reload])

  return { board, tasks, loading, error, reload }
}
